import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  AirIcon,
  ECGHeartIcon,
  MonitorHeartIcon,
} from "../../icons/qualityStatusIcons";
import FilterMenuButton from "../../common/FilterMenuButton";
import { useDateTimeFormat } from "../../../hooks/useDateTimeFormat";
import { getMeasurementModeDefinition } from "../../../utils/patientProfileData";
import {
  canOpenPatientRecordingEntry,
  isDashboardResultFeatureVisible,
} from "../../../utils/patientPresentationFeatures";
import EnvironmentalContextCell from "../../results/EnvironmentalContextCell";
import {
  filterMeasurementEntries,
  getMeasurementSpiroFilterValue,
  getMeasurementTypeFilterLabel,
  getMeasurementTypeFilterOptions,
} from "../../../utils/patientProfileFilters";
import { collapseSixMinuteWalkMeasurementEntries } from "../../../utils/results/sixMinuteWalk";
import { RECORDING_MODES } from "../../../utils/studies/studyconfig";

const recordingModesByValue = new Map(
  RECORDING_MODES.map((mode) => [mode.value, mode]),
);

const PASS_QUALITY_STATUSES = new Set(["pass", "passed", "good", "very good"]);

function createDefaultMeasurementFilters() {
  return {
    types: [],
    signalStatuses: [],
    spiroStatuses: [],
  };
}

function getMeasurementModeIcon(entry) {
  const definition = getMeasurementModeDefinition(entry);

  if (!definition?.modeValue) {
    return null;
  }

  return recordingModesByValue.get(definition.modeValue)?.Icon || null;
}

function normalizeQualityStatus(value) {
  if (value === undefined || value === null) {
    return "unknown";
  }

  const normalizedValue = String(value).trim().toLowerCase();

  if (
    !normalizedValue ||
    normalizedValue === "n/a" ||
    normalizedValue === "na"
  ) {
    return "unknown";
  }

  if (PASS_QUALITY_STATUSES.has(normalizedValue)) {
    return "pass";
  }

  return "fail";
}

function getQualityStatusLabel(status, t) {
  if (status === "pass") {
    return t("profilePage.statusPass", "Pass");
  }

  if (status === "unknown") {
    return t("profilePage.statusUnknown", "Unknown");
  }

  return t("profilePage.statusFail", "Fail");
}

function getSignalQualityStatus(entry) {
  return entry?.signalQualityStatus ?? entry?.resultSummary?.signalQualityStatus;
}

function getSpirometryQualityStatus(entry) {
  return (
    entry?.spiroSignalQualityStatus ?? entry?.resultSummary?.spiroSignalQualityStatus
  );
}

function getCardiacQualityStatus(entry) {
  return (
    entry?.cardiacQualityStatus ??
    entry?.resultSummary?.cardiacQualityStatus ??
    null
  );
}

function getQualityItems(
  entry,
  {
    showSignalQuality,
    showSpirometryQuality,
    showCardiacQuality,
    isManualAudioMeasurement,
    isSpirometryMeasurement,
    canHaveCardiacQuality,
    t,
  },
) {
  return [
    showSignalQuality && !isManualAudioMeasurement
      ? {
          key: "signal",
          Icon: MonitorHeartIcon,
          label: t("profilePage.respiratorySignal", "Respiratory signal"),
          status: normalizeQualityStatus(getSignalQualityStatus(entry)),
        }
      : null,
    showSpirometryQuality && isSpirometryMeasurement
      ? {
          key: "spirometry",
          Icon: AirIcon,
          label: t("studies.recordingModes.spirometry", "Spirometry"),
          status: normalizeQualityStatus(getSpirometryQualityStatus(entry)),
        }
      : null,
    showCardiacQuality && canHaveCardiacQuality
      ? {
          key: "cardiac",
          Icon: ECGHeartIcon,
          label: t("profilePage.cardiac", "Cardiac"),
          status: normalizeQualityStatus(getCardiacQualityStatus(entry)),
        }
      : null,
  ].filter(Boolean);
}

function QualityStatusIcon({ Icon, label, status, t, sizeClassName = "h-4 w-4" }) {
  const qualityStatusLabel = getQualityStatusLabel(status, t);
  const toneClassName =
    status === "pass"
      ? "text-primary transition-colors group-hover:text-white"
      : status === "unknown"
        ? "text-greyscale-3 transition-colors group-hover:text-white"
        : "text-red-700";

  return (
    <span
      role="img"
      aria-label={`${label}: ${qualityStatusLabel}`}
      title={`${label}: ${qualityStatusLabel}`}
      className={`inline-flex h-5 w-5 items-center justify-center ${toneClassName}`}
    >
      <Icon className={`${sizeClassName} fill-current`} aria-hidden="true" />
    </span>
  );
}

function MeasurementRow({
  entry,
  displayIndex,
  canViewResults,
  canOpenEntry,
  selected,
  onSelect,
  showQualitySummary,
  showEnvironmentalContext,
  showWeatherDetails,
  showSignalQuality,
  showSpirometryQuality,
  showCardiacQuality,
  t,
}) {
  const { formatDateTime } = useDateTimeFormat();
  const [qualityDetailsOpen, setQualityDetailsOpen] = useState(false);
  const qualityDetailsRef = useRef(null);
  const qualityDetailsId = useId();
  const dateTime = formatDateTime(entry?.takenAt);
  const modeLabel = getMeasurementTypeFilterLabel(entry, t);
  const ModeIcon = getMeasurementModeIcon(entry);
  const measurementModeValue = getMeasurementModeDefinition(entry)?.modeValue;
  const isSpirometryMeasurement = measurementModeValue === "spirometry";
  const isManualAudioMeasurement = measurementModeValue === "manual-audio";
  const canHaveCardiacQuality = Boolean(measurementModeValue) &&
    !isSpirometryMeasurement &&
    !isManualAudioMeasurement;
  const qualityItems = getQualityItems(entry, {
    showSignalQuality,
    showSpirometryQuality,
    showCardiacQuality,
    isManualAudioMeasurement,
    isSpirometryMeasurement,
    canHaveCardiacQuality,
    t,
  });

  useEffect(() => {
    if (!qualityDetailsOpen) {
      return undefined;
    }

    const handlePointerDownOutside = (event) => {
      if (qualityDetailsRef.current?.contains(event.target)) {
        return;
      }

      setQualityDetailsOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setQualityDetailsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDownOutside);
    document.addEventListener("touchstart", handlePointerDownOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDownOutside);
      document.removeEventListener("touchstart", handlePointerDownOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [qualityDetailsOpen]);

  const rowClassName = `border-b border-greyscale-1 transition-colors ${
    selected ? "bg-greyscale-1" : "bg-container"
  } ${canViewResults ? "hover:bg-greyscale-0.5" : ""}`;

  return (
    <tr className={rowClassName}>
      <td className="px-3 py-3 text-sm font-medium text-typography">
        {displayIndex}
      </td>

      <td className="px-3 py-3">
        <div className="flex min-w-0 items-center gap-2">
          {ModeIcon ? (
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-greyscale-1 text-primary">
              <ModeIcon className="h-4 w-4" aria-hidden="true" />
            </span>
          ) : null}

          <span
            className="truncate text-sm font-medium text-typography"
            title={modeLabel}
          >
            {modeLabel}
          </span>
        </div>
      </td>

      <td className="px-3 py-3 text-center text-sm text-typography">
        {dateTime.date || "--"}
      </td>

      <td className="px-3 py-3 text-center text-sm text-typography">
        {dateTime.time || "--"}
      </td>

      {showEnvironmentalContext ? (
        <td className="px-3 py-3 align-top">
          <EnvironmentalContextCell
            context={entry?.environmentalContext}
            t={t}
            showWeatherDetails={showWeatherDetails}
          />
        </td>
      ) : null}

      {showQualitySummary ? (
        <td className="px-3 py-3 text-center">
          <div className="flex items-center justify-center">
            {qualityItems.length ? (
              <div ref={qualityDetailsRef} className="relative">
                <button
                  type="button"
                  className="group inline-flex min-h-8 items-center justify-center gap-1.5 rounded-full bg-greyscale-1 px-2.5 py-1 transition-colors hover:bg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  aria-expanded={qualityDetailsOpen}
                  aria-haspopup="dialog"
                  aria-controls={qualityDetailsId}
                  aria-label={t(
                    "profilePage.qualityDetailsButton",
                    "Show quality details",
                  )}
                  onClick={() => setQualityDetailsOpen((current) => !current)}
                >
                  {qualityItems.map(({ key, Icon, label, status }) => (
                    <QualityStatusIcon
                      key={key}
                      Icon={Icon}
                      label={label}
                      status={status}
                      t={t}
                    />
                  ))}
                </button>

                {qualityDetailsOpen ? (
                  <div
                    id={qualityDetailsId}
                    role="dialog"
                    aria-label={t(
                      "profilePage.qualityDetailsDialog",
                      "Quality details",
                    )}
                    className="absolute left-1/2 top-full z-20 mt-2 w-56 -translate-x-1/2"
                  >
                    <div className="popover !w-auto !p-0 overflow-hidden text-left shadow-lg">
                      <div className="border-b border-greyscale-1 px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-greyscale-3">
                          {t("profilePage.qualitySummaryHeader", "Quality")}
                        </p>
                      </div>

                      <div className="flex flex-col gap-1 px-3 py-2">
                        {qualityItems.map(({ key, Icon, label, status }) => (
                          <div
                            key={key}
                            className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5"
                          >
                            <div className="flex min-w-0 items-center gap-2">
                              <QualityStatusIcon
                                Icon={Icon}
                                label={label}
                                status={status}
                                t={t}
                                sizeClassName="h-3.5 w-3.5"
                              />
                              <span className="truncate text-sm text-typography">
                                {label}
                              </span>
                            </div>

                            <span
                              className={`text-sm font-semibold ${
                                status === "pass"
                                  ? "text-primary"
                                  : status === "unknown"
                                    ? "text-greyscale-3"
                                    : "text-red-700"
                              }`}
                            >
                              {getQualityStatusLabel(status, t)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <span className="inline-flex items-center rounded-full bg-greyscale-1 px-2 py-1 text-xs font-medium text-typography">
                {t("profilePage.notApplicable", "N/A")}
              </span>
            )}
          </div>
        </td>
      ) : null}

      {canViewResults ? (
        <td className="px-3 py-3 text-center">
          {canOpenEntry ? (
            <button
              type="button"
              onClick={() => onSelect(entry)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-typography transition-colors hover:bg-greyscale-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label={t("profilePage.openRecording", "Open recording")}
            >
              <span
                className="material-symbols-outlined text-xl"
                aria-hidden="true"
              >
                more_vert
              </span>
            </button>
          ) : null}
        </td>
      ) : null}
    </tr>
  );
}

export default function PatientMeasurementsTable({
  entries = [],
  totalCount = 0,
  hasMore = false,
  loadingMore = false,
  loadingTrends = false,
  onLoadMore,
  canViewResults = false,
  activeEntry = null,
  canOpenTrends = false,
  dashboardResultFeatures = null,
  showEnvironmentalContext = false,
  showWeatherDetails = true,
  onOpenEntry,
  onOpenTrends,
  t,
}) {
  const [filters, setFilters] = useState(createDefaultMeasurementFilters);
  const scrollContainerRef = useRef(null);
  const showSignalQuality = isDashboardResultFeatureVisible(
    dashboardResultFeatures,
    "signalQuality",
  );
  const showSpirometryQuality = isDashboardResultFeatureVisible(
    dashboardResultFeatures,
    "spirometryQuality",
  );
  const showCardiacQuality = isDashboardResultFeatureVisible(
    dashboardResultFeatures,
    "cardiacQuality",
  );
  const showQualitySummary =
    showSignalQuality || showSpirometryQuality || showCardiacQuality;
  const displayEntries = useMemo(
    () => collapseSixMinuteWalkMeasurementEntries(entries),
    [entries],
  );

  useEffect(() => {
    setFilters(createDefaultMeasurementFilters());
  }, [
    displayEntries,
    showSignalQuality,
    showSpirometryQuality,
    showCardiacQuality,
  ]);

  // Prefetch the next page before the user reaches the very bottom so paging
  // feels seamless (no "Load more" click). Fires once per scroll frame; the
  // parent guards against overlapping fetches via `loadingMore`.
  const maybePrefetchMore = useCallback(() => {
    const container = scrollContainerRef.current;

    if (!container || !hasMore || loadingMore || loadingTrends || !onLoadMore) {
      return;
    }

    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;

    if (distanceFromBottom <= 400) {
      onLoadMore();
    }
  }, [hasMore, loadingMore, loadingTrends, onLoadMore]);

  const measurementTypeOptions = useMemo(
    () => getMeasurementTypeFilterOptions(displayEntries, t),
    [displayEntries, t],
  );

  const qualityOptions = useMemo(
    () => [
      {
        value: "pass",
        label: t("profilePage.statusPass", "Pass"),
      },
      {
        value: "fail",
        label: t("profilePage.statusFail", "Fail"),
      },
      {
        value: "na",
        label: t("profilePage.notApplicable", "N/A"),
      },
    ],
    [t],
  );

  const filteredEntries = useMemo(
    () => filterMeasurementEntries(displayEntries, filters),
    [displayEntries, filters],
  );
  const canOpenAnyEntries = useMemo(
    () =>
      canViewResults &&
      displayEntries.some((entry) =>
        canOpenPatientRecordingEntry(entry, dashboardResultFeatures),
      ),
    [canViewResults, dashboardResultFeatures, displayEntries],
  );

  const filterSections = useMemo(
    () =>
      [
        {
          key: "types",
          title: t("profilePage.questionnaireTypeHeader", "Type"),
          options: measurementTypeOptions,
        },
        showSignalQuality
          ? {
              key: "signalStatuses",
              title: t("profilePage.signalQualityHeader", "Signal"),
              options: qualityOptions,
            }
          : null,
        showSpirometryQuality
          ? {
              key: "spiroStatuses",
              title: t("profilePage.spirometryQualityHeader", "Spiro"),
              options: qualityOptions.filter(
                (option) =>
                  option.value !== "na" ||
                  displayEntries.some(
                    (entry) => getMeasurementSpiroFilterValue(entry) === "na",
                  ),
              ),
            }
          : null,
      ].filter(Boolean),
    [
      displayEntries,
      measurementTypeOptions,
      qualityOptions,
      showSignalQuality,
      showSpirometryQuality,
      t,
    ],
  );

  // Recording numbers are absolute: the newest recording is the participant's
  // total recording count, not the number currently fetched. Keyed by entry so
  // the number is stable regardless of filtering.
  const total = totalCount || entries.length;
  const numberByEntryKey = useMemo(() => {
    const map = new Map();
    entries.forEach((entry, index) => {
      map.set(`${entry.uid}-${entry.name}`, total - index);
    });
    return map;
  }, [entries, total]);

  const hasActiveFilters = Object.values(filters).some(
    (values) => values.length > 0,
  );

  const emptyMessage =
    displayEntries.length > 0 && hasActiveFilters
      ? t(
          "profilePage.noFilteredEntries",
          "No entries match the selected filters.",
        )
      : t("profilePage.noEntries");

  const toggleFilterValue = (sectionKey, value) => {
    setFilters((current) => {
      const nextValues = new Set(current[sectionKey] || []);

      if (nextValues.has(value)) {
        nextValues.delete(value);
      } else {
        nextValues.add(value);
      }

      return {
        ...current,
        [sectionKey]: [...nextValues],
      };
    });
  };

  return (
    <section className="card flex min-h-[360px] flex-col overflow-hidden lg:col-span-2">
      <div className="flex flex-col gap-3 border-b border-greyscale-1 p-2 sm:flex-row sm:items-end sm:justify-between">
        <h3 className="text-lg font-semibold">
          {t("profilePage.measurementsTableTitle", "Measurements")}
        </h3>

        <div className="flex flex-wrap items-center gap-2">
          <FilterMenuButton
            buttonClassName="small-outlined-button !min-h-0 !px-3 !py-1.5"
            buttonLabel={t("profilePage.measurementsFilterButton", "Filter")}
            clearLabel={t("profilePage.clearFilters", "Clear")}
            emptyLabel={t(
              "profilePage.noFilterOptions",
              "No filters available.",
            )}
            sections={filterSections}
            selectedValuesBySection={filters}
            onToggleValue={toggleFilterValue}
            onClearAll={() => setFilters(createDefaultMeasurementFilters())}
          />

          {canViewResults ? (
            <button
              type="button"
              className="small-primary-button !min-h-0 !px-4 !py-1.5 whitespace-nowrap disabled:opacity-60"
              disabled={!canOpenTrends || loadingTrends}
              onClick={onOpenTrends}
            >
              {t("profilePage.measurementTrendsButton", "Trends")}
            </button>
          ) : null}
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        onScroll={maybePrefetchMore}
        className="min-h-0 flex-1 overflow-auto bg-container"
      >
        <table
          className={`w-full table-fixed border-collapse ${
            canViewResults ? "min-w-[700px]" : "min-w-[540px]"
          }`}
        >
          <colgroup>
            <col className="w-16" />
          <col />
          <col className="w-28" />
          <col className="w-24" />
          {showEnvironmentalContext ? <col className="w-72" /> : null}
          {showQualitySummary ? <col className="w-32" /> : null}
          {canOpenAnyEntries ? <col className="w-20" /> : null}
        </colgroup>

          <thead className="sticky top-0 z-10 bg-container shadow-[0_1px_0_0_var(--color-greyscale-1)]">
            <tr className="border-b border-greyscale-1 text-sm text-typography">
              <th scope="col" className="px-3 py-3 text-left font-medium">
                {t("profilePage.hashHeader")}
              </th>

              <th scope="col" className="px-3 py-3 text-left font-medium">
                {t("studies.recordingModes.modeLabel", "Recording mode")}
              </th>

              <th scope="col" className="px-3 py-3 text-center font-medium">
                {t("profilePage.dateHeader")}
              </th>

              <th scope="col" className="px-3 py-3 text-center font-medium">
                {t("profilePage.timeHeader")}
              </th>

              {showEnvironmentalContext ? (
                <th scope="col" className="px-3 py-3 text-left font-medium">
                  {t("profilePage.environmentHeader", "Environment")}
                </th>
              ) : null}

              {showQualitySummary ? (
                <th scope="col" className="px-3 py-3 text-center font-medium">
                  {t("profilePage.qualitySummaryHeader", "Quality")}
                </th>
              ) : null}

              {canOpenAnyEntries ? (
                <th scope="col" className="px-3 py-3 text-center font-medium">
                  {t("profilePage.actionHeader", "Actions")}
                </th>
              ) : null}
            </tr>
          </thead>

          <tbody>
            {filteredEntries.length ? (
              filteredEntries.map((entry, index, items) => (
                <MeasurementRow
                  key={`${entry.uid}-${entry.name}`}
                  entry={entry}
                  displayIndex={
                    numberByEntryKey.get(`${entry.uid}-${entry.name}`) ??
                    items.length - index
                  }
                  canViewResults={canViewResults}
                  selected={
                    activeEntry?.uid === entry.uid &&
                    activeEntry?.name === entry.name
                  }
                  canOpenEntry={canOpenPatientRecordingEntry(
                    entry,
                    dashboardResultFeatures,
                  )}
                  onSelect={onOpenEntry}
                  showQualitySummary={showQualitySummary}
                  showEnvironmentalContext={showEnvironmentalContext}
                  showWeatherDetails={showWeatherDetails}
                  showSignalQuality={showSignalQuality}
                  showSpirometryQuality={showSpirometryQuality}
                  showCardiacQuality={showCardiacQuality}
                  t={t}
                />
              ))
            ) : (
              <tr>
                <td
                  colSpan={
                    4 +
                    (showEnvironmentalContext ? 1 : 0) +
                    (showQualitySummary ? 1 : 0) +
                    (canOpenAnyEntries ? 1 : 0)
                  }
                  className="px-4 py-10 text-center text-greyscale-3"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {hasMore ? (
          <div className="flex justify-center border-t border-greyscale-1 p-3">
            <button
              type="button"
              className="small-outlined-button !min-h-0 !px-4 !py-1.5 disabled:opacity-60"
              onClick={onLoadMore}
              disabled={loadingMore || loadingTrends || !onLoadMore}
            >
              {loadingMore || loadingTrends
                ? t("profilePage.loadingMore", "Loading…")
                : t("profilePage.loadMore", "Load more")}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
